import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    ArrowLeft,
    Star,
    Minus,
    Plus,
    ShoppingBag,
    Truck,
    ShieldCheck,
    RotateCcw,
    Check,
} from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewsSection from "@/components/ReviewsSection";
import ProductSpecifications from "@/components/ProductSpecifications";
import { useBreadcrumbLabel } from "@/contexts/BreadcrumbContext";

export default function ProductDetailPage({ onOpenAuth }) {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [activeImage, setActiveImage] = useState("");
    const [qty, setQty] = useState(1);
    const [attributes, setAttributes] = useState([]);
    const [picked, setPicked] = useState({}); // { attribute_id: value_id }
    const { addItem } = useCart();

    useBreadcrumbLabel(`/products/${id}`, product?.name || null);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api
            .get(`/products/${id}`)
            .then(({ data }) => {
                setProduct(data);
                setActiveImage(data.image_url);
                setQty(1);
                setPicked({});
            })
            .catch((e) => {
                if (e.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        if (product?.attribute_ids?.length) {
            api.get("/attributes").then(({ data }) => setAttributes(data)).catch(() => setAttributes([]));
        }
    }, [product?.attribute_ids]);

    const productAttributes = useMemo(() => {
        if (!product?.attribute_ids?.length || !attributes.length) return [];
        return product.attribute_ids
            .map((aid) => attributes.find((a) => a.id === aid))
            .filter(Boolean);
    }, [product, attributes]);

    // Pick the matching variant for the current attribute selection
    const selectedVariant = useMemo(() => {
        if (!product?.variants?.length) return null;
        if (productAttributes.length === 0) return null;
        const want = productAttributes.map((a) => [a.id, picked[a.id]]);
        if (want.some(([, v]) => !v)) return null;
        return product.variants.find((v) => want.every(([aid, vid]) => (v.attributes || {})[aid] === vid)) || null;
    }, [product, productAttributes, picked]);

    const effectivePrice = selectedVariant?.price ?? product?.price ?? 0;
    const effectiveStock = product?.variants?.length
        ? (selectedVariant?.stock ?? 0)
        : (product?.stock ?? 0);

    const stockStatus = useMemo(() => {
        if (!product) return null;
        if (product.variants?.length && !selectedVariant) return { label: "Choose options to see availability", tone: "low" };
        if (effectiveStock <= 0) return { label: "Out of stock", tone: "out" };
        if (effectiveStock <= 5) return { label: `Only ${effectiveStock} left`, tone: "low" };
        return { label: `In stock (${effectiveStock} available)`, tone: "ok" };
    }, [product, selectedVariant, effectiveStock]);

    const allImages = useMemo(() => {
        if (!product) return [];
        const list = [product.image_url, ...(product.gallery || [])];
        return Array.from(new Set(list));
    }, [product]);

    const maxQty = product ? Math.min(effectiveStock || 0, 10) : 1;

    const variantLabel = useMemo(() => {
        if (!selectedVariant) return null;
        return productAttributes.map((a) => {
            const vid = selectedVariant.attributes[a.id];
            const v = a.values?.find((x) => x.id === vid);
            return v ? `${a.name}: ${v.value}` : "";
        }).filter(Boolean).join(" / ");
    }, [selectedVariant, productAttributes]);

    const handleAdd = () => {
        if (!product) return;
        if (product.variants?.length && !selectedVariant) {
            toast.error("Please select all options");
            return;
        }
        if (effectiveStock <= 0) return;
        addItem(
            {
                id: product.id,
                kind: "product",
                name: product.name,
                price: effectivePrice,
                image_url: product.image_url,
                meta: product.category,
                variant_id: selectedVariant?.id || null,
                variant_label: variantLabel || null,
            },
            qty
        );
        toast.success("Added to cart", {
            description: `${qty} × ${product.name}${variantLabel ? ` (${variantLabel})` : ""}`,
        });
    };

    return (
        <div className="min-h-screen bg-paper" data-testid="product-detail-page">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-brand-600"
                        data-testid="product-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                </div>

                {loading && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="grid lg:grid-cols-12 gap-12">
                            <div className="lg:col-span-7 h-[520px] rounded-3xl bg-gray-100 animate-pulse" />
                            <div className="lg:col-span-5 h-[520px] rounded-3xl bg-gray-100 animate-pulse" />
                        </div>
                    </div>
                )}

                {!loading && notFound && (
                    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                        <h1 className="font-heading text-3xl font-semibold text-ink-900">
                            Product not found
                        </h1>
                        <p className="mt-3 text-ink-700">
                            The product you're looking for doesn't exist.
                        </p>
                        <Button
                            asChild
                            className="mt-6 rounded-full bg-brand-500 hover:bg-brand-600"
                        >
                            <Link to="/">Browse Products</Link>
                        </Button>
                    </div>
                )}

                {!loading && product && (
                    <>
                        <section className="py-8 md:py-12">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
                                {/* Gallery */}
                                <div className="lg:col-span-7 animate-fade-up">
                                    <div className="aspect-square w-full rounded-3xl overflow-hidden bg-paper-alt shadow-soft border border-gray-100">
                                        <img
                                            key={activeImage}
                                            src={activeImage}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                            data-testid="product-active-image"
                                        />
                                    </div>
                                    {allImages.length > 1 && (
                                        <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-thumbnails">
                                            {allImages.map((src) => (
                                                <button
                                                    key={src}
                                                    onClick={() => setActiveImage(src)}
                                                    className={`aspect-square rounded-2xl overflow-hidden border-2 transition ${
                                                        activeImage === src
                                                            ? "border-brand-500 ring-2 ring-brand-200"
                                                            : "border-gray-200 hover:border-brand-300"
                                                    }`}
                                                    data-testid={`product-thumb-${allImages.indexOf(src)}`}
                                                >
                                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Detail */}
                                <aside className="lg:col-span-5 animate-fade-up [animation-delay:120ms] space-y-5">
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to="/#products"
                                            className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-700 hover:text-brand-800"
                                            data-testid="product-category-link"
                                        >
                                            {product.category}
                                        </Link>
                                        <span className="text-ink-400">&middot;</span>
                                        <span className="inline-flex items-center gap-1 text-xs text-ink-700">
                                            <Star className="h-3.5 w-3.5 fill-accent-400 text-accent-400" />
                                            <span className="font-semibold text-ink-900">
                                                {Number(product.rating ?? 4.8).toFixed(1)}
                                            </span>
                                            <span>(218 reviews)</span>
                                        </span>
                                    </div>

                                    <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900 leading-tight">
                                        {product.name}
                                    </h1>

                                    <p className="text-base md:text-lg text-ink-700 leading-relaxed">
                                        {product.description}
                                    </p>

                                    <div className="flex items-baseline gap-2">
                                        <div className="font-heading text-4xl font-semibold text-ink-900" data-testid="product-price">
                                            ₹{Number(effectivePrice).toLocaleString("en-IN")}
                                        </div>
                                        <div className="text-sm text-ink-700">incl. GST</div>
                                    </div>

                                    {/* Variant selectors */}
                                    {productAttributes.length > 0 && (
                                        <div className="space-y-3 pt-1" data-testid="product-variant-selectors">
                                            {productAttributes.map((a) => (
                                                <div key={a.id}>
                                                    <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2">
                                                        {a.name}
                                                        {picked[a.id] && (
                                                            <span className="ml-2 text-ink-700 normal-case font-normal">
                                                                {a.values.find((v) => v.id === picked[a.id])?.value}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {(a.values || []).map((v) => {
                                                            const active = picked[a.id] === v.id;
                                                            return (
                                                                <button
                                                                    key={v.id}
                                                                    type="button"
                                                                    onClick={() => setPicked((p) => ({ ...p, [a.id]: v.id }))}
                                                                    className={`min-w-12 h-10 px-4 rounded-full border-2 text-sm transition ${active ? "border-brand-500 bg-brand-50 text-brand-700 font-semibold" : "border-gray-200 hover:border-brand-300 text-ink-900"}`}
                                                                    data-testid={`variant-option-${a.id}-${v.id}`}
                                                                >
                                                                    {v.value}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {stockStatus && (
                                        <div
                                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
                                                stockStatus.tone === "out"
                                                    ? "bg-red-50 text-red-700"
                                                    : stockStatus.tone === "low"
                                                    ? "bg-accent-50 text-accent-700"
                                                    : "bg-green-50 text-green-700"
                                            }`}
                                            data-testid="product-stock-status"
                                        >
                                            <span
                                                className={`h-1.5 w-1.5 rounded-full ${
                                                    stockStatus.tone === "out"
                                                        ? "bg-red-600"
                                                        : stockStatus.tone === "low"
                                                        ? "bg-accent-500"
                                                        : "bg-green-600"
                                                }`}
                                            />
                                            {stockStatus.label}
                                        </div>
                                    )}

                                    {product.tags?.length > 0 && (
                                        <div className="flex flex-wrap gap-2 pt-1" data-testid="product-tags">
                                            {product.tags.map((t) => (
                                                <span
                                                    key={t}
                                                    className="px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium"
                                                    data-testid={`product-tag-${t}`}
                                                >
                                                    #{t}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Quantity + Add */}
                                    <div className="pt-2 space-y-3">
                                        <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold">
                                            Quantity
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div
                                                className="inline-flex items-center gap-1 bg-paper-alt rounded-full p-1"
                                                data-testid="product-qty-control"
                                            >
                                                <button
                                                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                                                    disabled={qty <= 1}
                                                    className="h-9 w-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-brand-300 hover:text-brand-600 disabled:opacity-40"
                                                    aria-label="Decrease quantity"
                                                    data-testid="product-qty-decrease"
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </button>
                                                <span
                                                    className="px-3 text-sm font-semibold w-10 text-center"
                                                    data-testid="product-qty-value"
                                                >
                                                    {qty}
                                                </span>
                                                <button
                                                    onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                                                    disabled={qty >= maxQty}
                                                    className="h-9 w-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-brand-300 hover:text-brand-600 disabled:opacity-40"
                                                    aria-label="Increase quantity"
                                                    data-testid="product-qty-increase"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            </div>
                                            <Button
                                                onClick={handleAdd}
                                                disabled={effectiveStock <= 0 || (product.variants?.length > 0 && !selectedVariant)}
                                                className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-7 py-6 text-base flex-1 sm:flex-initial"
                                                data-testid="product-add-to-cart-btn"
                                            >
                                                <ShoppingBag className="h-4 w-4 mr-2" />
                                                {effectiveStock <= 0
                                                    ? "Out of Stock"
                                                    : (product.variants?.length > 0 && !selectedVariant)
                                                        ? "Choose Options"
                                                        : "Add to Cart"}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Trust strip */}
                                    <ul className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100">
                                        <li className="flex items-center gap-2 text-xs text-ink-700">
                                            <Truck className="h-4 w-4 text-brand-500 shrink-0" />
                                            <span>Free shipping above ₹999</span>
                                        </li>
                                        <li className="flex items-center gap-2 text-xs text-ink-700">
                                            <ShieldCheck className="h-4 w-4 text-brand-500 shrink-0" />
                                            <span>2-year quality warranty</span>
                                        </li>
                                        <li className="flex items-center gap-2 text-xs text-ink-700">
                                            <RotateCcw className="h-4 w-4 text-brand-500 shrink-0" />
                                            <span>14-day easy returns</span>
                                        </li>
                                    </ul>
                                </aside>
                            </div>
                        </section>

                        {/* Long description */}
                        <section className="py-12 md:py-16 bg-paper-alt">
                            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                                <h2 className="font-heading text-3xl font-semibold text-ink-900">
                                    Product details
                                </h2>
                                {product.long_description ? (
                                    <p
                                        className="mt-5 text-base md:text-lg text-ink-700 leading-relaxed whitespace-pre-line"
                                        data-testid="product-long-description"
                                    >
                                        {product.long_description}
                                    </p>
                                ) : (
                                    <p className="mt-5 text-base text-ink-700">
                                        {product.description}
                                    </p>
                                )}

                                {/* Highlights derived from tags */}
                                {product.tags?.length > 0 && (
                                    <div className="mt-10 grid sm:grid-cols-2 gap-3">
                                        {product.tags.slice(0, 6).map((t) => (
                                            <div
                                                key={t}
                                                className="flex items-start gap-2.5 text-sm text-ink-900 bg-white rounded-2xl border border-gray-100 px-4 py-3"
                                            >
                                                <span className="mt-0.5 h-5 w-5 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                                                    <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                                                </span>
                                                <span className="capitalize">{t}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <ProductSpecifications product={product} />
                            </div>

                            <ReviewsSection itemId={product.id} itemKind="product" onOpenAuth={onOpenAuth} />
                        </section>
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}
